import React, { useContext, useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import AuthContext from "../contexts/AuthContext";
import { Navbar } from "../components/Navbar";
import { JobPostings } from "../components/JobPostings";
import { StudiosList } from "../components/StudiosList";
import RequireAuthPopup from "../components/RequireAuthPopup";
import axios from "axios";

export default function LandingPage() {
  const { user } = useContext(AuthContext);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [authPopupOpen, setAuthPopupOpen] = useState(false);
  const [createJobOpen, setCreateJobOpen] = useState(false);
  const [jobDetails, setJobDetails] = useState({
    title: "",
    shortDescription: "",
    longDescription: "",
    budget: "",
    location: "Remote",
    deadline: "",
    tags: "",
    email: "",
  });
  const [loading, setLoading] = useState(false);

  const handleProtectedAction = () => {
    if (!user) {
      setAuthPopupOpen(true);
    } else {
      setCreateJobOpen(true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setJobDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const tagsArray = jobDetails.tags.split(",").map((tag) => tag.trim());
      const payload = { ...jobDetails, tags: tagsArray };

      await axios.post(`${process.env.REACT_APP_API_URL}/api/works`, payload, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${user?.token}`,
          "Content-Type": "application/json",
        },
      });

      setCreateJobOpen(false);
      setJobDetails({
        title: "",
        shortDescription: "",
        longDescription: "",
        budget: "",
        location: "Remote",
        deadline: "",
        tags: "",
      });
      alert("Job created successfully!");
    } catch (error) {
      console.error("Error creating job:", error.message);
      alert("Failed to create the job.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Require Auth Popup */}
      <RequireAuthPopup
        isOpen={authPopupOpen}
        onClose={() => setAuthPopupOpen(false)}
      />

      {/* Create Job Popup */}
      <Transition appear show={createJobOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setCreateJobOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-semibold leading-6 text-gray-900 border-b pb-2"
                  >
                    Create a Job
                  </Dialog.Title>
                  <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                    <div>
                      <label
                        htmlFor="title"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Job Title
                      </label>
                      <input
                        id="title"
                        name="title"
                        type="text"
                        value={jobDetails.title}
                        onChange={handleInputChange}
                        placeholder="Enter job title"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="shortDescription"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Short Description
                      </label>
                      <input
                        id="shortDescription"
                        name="shortDescription"
                        type="text"
                        value={jobDetails.shortDescription}
                        onChange={handleInputChange}
                        placeholder="Enter a short description"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="longDescription"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Long Description
                      </label>
                      <textarea
                        id="longDescription"
                        name="longDescription"
                        value={jobDetails.longDescription}
                        onChange={handleInputChange}
                        placeholder="Enter detailed description"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        rows={6}
                        required
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="budget"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Budget
                      </label>
                      <input
                        id="budget"
                        name="budget"
                        type="number"
                        value={jobDetails.budget}
                        onChange={handleInputChange}
                        placeholder="Enter budget amount"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="location"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Location
                      </label>
                      <input
                        id="location"
                        name="location"
                        type="text"
                        value={jobDetails.location}
                        onChange={handleInputChange}
                        placeholder="Enter location (default: Remote)"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="deadline"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Deadline
                      </label>
                      <input
                        id="deadline"
                        name="deadline"
                        type="date"
                        value={jobDetails.deadline}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                      <p className="mt-1 mb-2 text-xs text-gray-400">
                        Please choose a deadline at least 24 hours from now.
                      </p>
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Contact Email
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={jobDetails.email}
                        onChange={handleInputChange}
                        placeholder="Enter contact email"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="tags"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Tags (comma-separated)
                      </label>
                      <input
                        id="tags"
                        name="tags"
                        type="text"
                        value={jobDetails.tags}
                        onChange={handleInputChange}
                        placeholder="e.g., design, development, marketing"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setCreateJobOpen(false)}
                        className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:bg-gray-400"
                      >
                        {loading ? "Saving..." : "Save Job"}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Navbar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <main className="lg:pl-72">
        <div className="xl:pr-96">
          <div className="px-4 py-10 sm:px-6 lg:px-8 lg:py-6">
            <div className="border-b border-gray-200 bg-white px-4 py-5 sm:px-6 rounded-2xl">
              <div className="flex flex-col sm:flex-row justify-between items-center">
                <h3 className="text-base font-semibold text-gray-900 mb-4 sm:mb-0">
                  {user
                    ? `Welcome back, ${
                        user.displayName
                          ? user.displayName.charAt(0).toUpperCase() +
                            user.displayName.slice(1)
                          : user.email.split("@")[0].charAt(0).toUpperCase() +
                            user.email.split("@")[0].slice(1)
                      }!`
                    : "Sign in to apply for gigs or save your favorite postings."}
                </h3>
                <button
                  onClick={handleProtectedAction}
                  className="w-full sm:w-auto flex items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2"
                >
                  Create Job
                </button>
              </div>
              <JobPostings />
            </div>
          </div>
        </div>
      </main>

      <aside className="fixed inset-y-0 right-0 hidden w-96 overflow-y-auto border-l border-gray-200 px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-2xl lg:my-6 lg:mx-2">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Studios</h2>
        <StudiosList />
      </aside>
    </>
  );
}
